import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import {
  Button,
  InputGroup,
  Intent,
  TagInput,
  Spinner,
  TextArea,
  Tooltip,
  Alert,
} from '@blueprintjs/core'
import { withApollo, WithApolloClient } from 'react-apollo'
import gql from 'graphql-tag'
import Icon from './UI/FAIcon'
import { ApolloClient } from 'apollo-boost'
import { athenaUrl } from '../backend'

export interface IGlobalSetting {
  id: string
  key: string
  description: string
  value: string[]
}

interface IProps {
  client?: ApolloClient<any>
}

const globalMapQuery = gql`
  query globalSettings {
    globalSettings {
      id
      key
      value
      description
    }
  }
`

const updateGlobalSettingMutation = gql`
  mutation updateGlobalSetting($input: UpdateGlobalSettingInput!) {
    updateGlobalSetting(input: $input) {
      globalSettings {
        id
        key
        value
        description
      }
    }
  }
`

const createGlobalSettingMutation = gql`
  mutation createGlobalSetting($input: CreateGlobalSettingInput!) {
    createGlobalSetting(input: $input) {
      globalSettings {
        id
        key
        value
        description
      }
    }
  }
`

const deleteGlobalSettingMutation = gql`
  mutation deleteGlobalSetting($input: DeleteGlobalSettingInput!) {
    deleteGlobalSetting(input: $input) {
      globalSettings {
        id
        key
        value
      }
      errors
    }
  }
`

interface IPayload {
  globalSettings: {
    id: string
    key: string
    value: string | string[]
    description: string
  }[]
}

function GlobalMap({ client }: WithApolloClient<IProps>) {
  const [rows, setRows] = useState<IGlobalSetting[]>([])
  const [originalRows, setOriginalRows] = useState<IGlobalSetting[]>(
    JSON.parse(JSON.stringify([]))
  )
  const [init, setInit] = useState(false)
  const [hasChanges, setHasChanges] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [rowToDelete, setRowToDelete] = useState<{
    row: IGlobalSetting
    index: number
  } | null>(null)

  const fetchData = () => {
    setLoading(true)
    setError(null)

    client
      .query({
        query: globalMapQuery,
        fetchPolicy: 'network-only',
      })
      .then((result: any) => {
        if (result.data && result.data.globalSettings) {
          const loadedData = JSON.parse(
            JSON.stringify(result.data.globalSettings)
          )
          loadedData.forEach((d: any) => {
            d.value = d.value.split(', ')
          })
          setRows(loadedData)
          setOriginalRows(JSON.parse(JSON.stringify(loadedData)))
        }
        setLoading(false)
      })
      .catch((err: any) => {
        console.error('Error loading data:', err)
        setError(err.message)
        setLoading(false)
      })
  }

  useEffect(() => {
    fetchData()
    setInit(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [init])

  useEffect(() => {
    // Check if data has changed
    const dataChanged = JSON.stringify(rows) !== JSON.stringify(originalRows)
    setHasChanges(dataChanged)
  }, [rows, originalRows])

  const updateRow = (
    index: number,
    field: keyof IGlobalSetting,
    value: string | string[]
  ) => {
    const updatedRows = [...rows]
    updatedRows[index] = { ...updatedRows[index], [field]: value }
    setRows(updatedRows)
  }

  const addRow = () => {
    if (hasChanges) {
      return // Prevent adding row when there are unsaved changes
    }
    const newRow: IGlobalSetting = {
      id: `row-${Date.now()}`,
      key: '',
      description: '',
      value: [],
    }
    const updatedRows = [...rows, newRow]
    setRows(updatedRows)
  }

  const removeRow = (row: IGlobalSetting, index: number) => {
    if (hasChanges) {
      return // Prevent removing row when there are unsaved changes
    }
    // Open confirmation dialog
    setRowToDelete({ row, index })
    setDeleteConfirmOpen(true)
  }

  const confirmDelete = () => {
    if (!rowToDelete) return

    const { row, index } = rowToDelete

    if (row.id.startsWith('row-')) {
      const updatedRows = rows.filter((_, i) => i !== index)
      setRows(updatedRows)
    } else {
      client
        .mutate({
          mutation: deleteGlobalSettingMutation,
          variables: {
            input: {
              ids: [row.id],
            },
          },
        })
        .then(() => {
          fetchData()
        })
        .catch((error: any) => {
          console.error('Error deleting row:', error)
        })
    }

    setDeleteConfirmOpen(false)
    setRowToDelete(null)
  }

  const cancelDelete = () => {
    setDeleteConfirmOpen(false)
    setRowToDelete(null)
  }

  const handleTagsChange = (index: number, values: React.ReactNode[]) => {
    // Convert React nodes to strings
    const stringValues = values
      .map((v) => (typeof v === 'string' ? v : ''))
      .filter((v) => v.trim() !== '')
    updateRow(index, 'value', stringValues)
  }

  const handleCancel = () => {
    setRows(JSON.parse(JSON.stringify(originalRows)))
    setHasChanges(false)
  }

  const [saving, setSaving] = useState(false)

  const handleSave = () => {
    setSaving(true)
    let isNewRowPresent = false
    // Filter only changed rows
    const changedRows = rows.filter((row, index) => {
      // New rows (with temporary IDs) are always included
      if (row.id.startsWith('row-')) {
        isNewRowPresent = true
        return true
      }
      // For existing rows, check if they've changed
      const originalRow = originalRows[index]
      if (!originalRow) return true
      return JSON.stringify(row) !== JSON.stringify(originalRow)
    })

    client
      .mutate({
        mutation: isNewRowPresent
          ? createGlobalSettingMutation
          : updateGlobalSettingMutation,
        variables: {
          input: {
            settings: changedRows.map((e) =>
              globalSettingSaveFormat(e, isNewRowPresent)
            ),
          },
        },
      })
      .then((result: any) => {
        // Refresh data from server after successful save
        fetchData()
        setHasChanges(false)
        setSaving(false)
      })
      .catch((error: any) => {
        console.error('Error saving data:', error)
        setSaving(false)
      })
  }

  if (loading) {
    return (
      <Container>
        <LoadingContainer>
          <Spinner />
        </LoadingContainer>
      </Container>
    )
  }

  if (error) {
    return (
      <Container>
        <ErrorContainer>
          <Icon name="exclamation-triangle" style={{ marginRight: 10 }} />
          Error loading data: {error}
        </ErrorContainer>
      </Container>
    )
  }

  return (
    <Container>
      <TableContainer>
        <HeaderRow>
          <HeaderCell style={{ flex: '1 1 20%' }}>Key</HeaderCell>
          <HeaderCell style={{ flex: '1 1 25%' }}>Description</HeaderCell>
          <HeaderCell style={{ flex: '1 1 50%' }}>Value</HeaderCell>
          <HeaderCell style={{ flex: '0 0 50px' }}>Actions</HeaderCell>
        </HeaderRow>

        {rows.map((row, index) => {
          // Check if this is a new row (has the temporary id format)
          const isNewRow = row.id.startsWith('row-')

          return (
            <Row key={row.id}>
              <Cell style={{ flex: '1 1 20%' }}>
                <InputGroup
                  placeholder="Enter key..."
                  value={row.key}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    updateRow(index, 'key', e.target.value)
                  }
                  readOnly={!isNewRow}
                  onKeyDown={
                    isNewRow
                      ? (e: React.KeyboardEvent) => {
                          if (e.key === 'Enter') {
                            e.preventDefault()
                          }
                        }
                      : undefined
                  }
                />
              </Cell>
              <Cell style={{ flex: '1 1 25%', alignItems: 'flex-start' }}>
                <TextArea
                  placeholder="Enter description..."
                  value={row.description}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                    updateRow(index, 'description', e.target.value)
                  }
                  fill
                  style={{
                    minHeight: '38px',
                    maxHeight: '6em',
                    resize: 'vertical',
                    lineHeight: '1.5em',
                  }}
                  onKeyDown={(e: React.KeyboardEvent) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault()
                    }
                  }}
                />
              </Cell>
              <Cell
                style={{
                  flex: '1 1 50%',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}>
                <StyledTagInput
                  values={row.value}
                  onChange={(values) => handleTagsChange(index, values)}
                  placeholder="Type and press Enter to add..."
                  tagProps={{ minimal: true }}
                  addOnBlur={true}
                  separator=","
                  fill
                  onKeyDown={(e: React.KeyboardEvent) => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                    }
                  }}
                />
              </Cell>
              <Cell style={{ flex: '0 0 50px', justifyContent: 'center' }}>
                <Tooltip
                  content="Cannot delete until changes are saved."
                  disabled={!hasChanges}>
                  <Button
                    minimal
                    small
                    intent={Intent.DANGER}
                    icon={
                      <Icon
                        name="trash"
                        style={{ color: 'var(--danger-color)' }}
                      />
                    }
                    onClick={() => removeRow(row, index)}
                    disabled={hasChanges}
                  />
                </Tooltip>
              </Cell>
            </Row>
          )
        })}

        <AddButtonBar>
          <Tooltip
            content="Cannot add until changes are saved."
            disabled={!hasChanges}>
            <Button
              intent={Intent.PRIMARY}
              icon={<Icon name="plus" style={{ marginTop: 3 }} />}
              onClick={addRow}
              disabled={hasChanges}>
              Add Row
            </Button>
          </Tooltip>
        </AddButtonBar>
      </TableContainer>

      {hasChanges && (
        <ActionBar>
          <ActionBarContent>
            <ActionBarMessage>
              <Icon
                name="info-circle"
                style={{
                  marginRight: 10,
                  color: 'var(--primary-button-background)',
                }}
              />
              You have unsaved changes
            </ActionBarMessage>
            <ActionBarButtons>
              <Button
                minimal
                onClick={handleCancel}
                disabled={saving}
                style={{ marginRight: 10 }}>
                Cancel
              </Button>
              <Button
                intent={Intent.PRIMARY}
                onClick={handleSave}
                loading={saving}>
                Save Changes
              </Button>
            </ActionBarButtons>
          </ActionBarContent>
        </ActionBar>
      )}

      <Alert
        isOpen={deleteConfirmOpen}
        intent={Intent.DANGER}
        icon="trash"
        onCancel={cancelDelete}
        onConfirm={confirmDelete}
        cancelButtonText="Cancel"
        confirmButtonText="Delete">
        <p style={{ color: 'white' }}>
          Are you sure you want to delete the setting with key{' '}
          <strong>"{rowToDelete?.row.key}"</strong>?
          {!rowToDelete?.row.id.startsWith('row-') && (
            <span> This action cannot be undone.</span>
          )}
        </p>
      </Alert>
    </Container>
  )
}

const globalSettingSaveFormat = (row: any, isNewRowPresent: boolean) => {
  return isNewRowPresent
    ? {
        key: row.key,
        description: row.description,
        value: row.value.join(', '),
      }
    : {
        id: row.id,
        key: row.key,
        description: row.description,
        value: row.value.join(', '),
      }
}

export default withApollo<IProps>(GlobalMap)

const Container = styled.div`
  padding: 20px;
  padding-bottom: 10px;
  color: var(--text-color);
  position: relative;
`

const TableContainer = styled.div`
  max-height: calc(100vh - 200px);
  overflow-y: auto;
  overflow-x: hidden;
  margin-bottom: 50px;
`

const AddButtonBar = styled.div`
  position: absolute;
  bottom: 0;
  z-index: 5;
  display: flex;
  justify-content: flex-end;
  padding: 10px;
`

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 50px;
`

const ErrorContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;
  color: var(--danger-color);
  background: var(--inactive-card-background);
  border-radius: var(--border-radius);
`

const ActionBar = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 10;
  padding: 15px 20px;
  background: var(--tooltip-background-color);
  border-top: 1px solid var(--border-color);
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.1);
`

const ActionBarContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const ActionBarMessage = styled.div`
  display: flex;
  align-items: center;
  font-size: 16px;
  color: var(--tooltip-text-color);
`

const ActionBarButtons = styled.div`
  display: flex;
  align-items: center;
`

const HeaderRow = styled.div`
  display: flex;
  gap: 10px;
  padding: 10px;
  background: var(--active-card-background);
  border-radius: var(--border-radius);
  margin-bottom: 10px;
  font-weight: 600;
`

const HeaderCell = styled.div`
  padding: 5px;
  color: var(--text-color);
`

const Row = styled.div`
  display: flex;
  gap: 10px;
  padding: 10px;
  margin-bottom: 10px;
  background: var(--inactive-card-background);
  border-radius: var(--border-radius);
  align-items: center;
`

const Cell = styled.div`
  display: flex;
  align-items: center;
  padding: 5px;
`

const StyledTagInput = styled(TagInput)`
  .bp3-tag-input-values {
    font-family: -apple-system, 'BlinkMacSystemFont', 'Segoe UI', 'Roboto',
      'Oxygen', 'Ubuntu', 'Cantarell', 'Open Sans', 'Helvetica Neue', 'Icons16',
      sans-serif;
    font-size: 14px;
    color: var(--text-color);
  }

  .bp3-input-ghost {
    font-family: -apple-system, 'BlinkMacSystemFont', 'Segoe UI', 'Roboto',
      'Oxygen', 'Ubuntu', 'Cantarell', 'Open Sans', 'Helvetica Neue', 'Icons16',
      sans-serif;
    font-size: 14px;
    color: var(--text-color);
  }

  input::placeholder {
    color: rgba(92, 112, 128, 0.6);
  }

  input.bp5-input-ghost {
    color: white;
    caret-color: white;
  }

  .bp5-text-overflow-ellipsis.bp5-fill {
    color: var(--text-color);
    text-transform: none;
    font-size: 14px;
    font-weight: 400;
  }

  .bp3-tag-remove,
  .bp5-tag-remove {
    color: white !important;
    opacity: 0.7;
  }

  .bp3-tag-remove:hover,
  .bp5-tag-remove:hover {
    opacity: 1;
  }
`
